/**
 * Drive H-connector transfer: wait → close → shift.
 *
 *  1) OHT arrives on either leg (home or far).
 *  2) If that leg is open (dashed): pause OHT, animate solid bar onto it (close).
 *  3) Once closed (solid red under OHT): animate solid bar + OHT to the other track.
 *  4) Land on destination; continue along that track.
 */
import { LayoutElement } from '../types';
import {
  CONNECTOR_SHIFT_DURATION_MS,
  buildConnectorShiftUpdates,
  findConnectorApproachForOht,
  getConnectorFarLegMid,
  getConnectorHomeLegMid,
  getConnectorReleaseRadius,
  getConnectorShiftState,
  getConnectorSolidBarPose,
  getConnectorSolidLegMid,
  alignHeadingToConnectorLeg,
  isConnectorElement,
  type ConnectorShiftState,
} from './connectorRail';
import { connectorShiftStore } from '../store/connectorShiftStore';

/** Close (open→closed) is snappier than the full cross-track transfer. */
export const CONNECTOR_CLOSE_DURATION_MS = Math.round(CONNECTOR_SHIFT_DURATION_MS * 0.55);

export type ConnectorShiftComplete = {
  id: string;
  updates: Partial<LayoutElement>;
  ohtId?: string;
  /** Set only when a transfer lands (not on close-only patches). */
  landAt?: { x: number; y: number; angle: number };
  alongHeadingDeg?: number;
};

export type ConnectorRidingPose = {
  ohtId: string;
  connectorId: string;
  x: number;
  y: number;
  angle: number;
};

export type ConnectorShiftTickResult = {
  completed: ConnectorShiftComplete[];
  /** OHT poses while riding the solid bar across. */
  riding: ConnectorRidingPose[];
  /** OHT poses while waiting for close (snapped to approach mid). */
  waiting: ConnectorRidingPose[];
};

function oppositeSide(side: ConnectorShiftState): ConnectorShiftState {
  return side === 'home' ? 'shifted' : 'home';
}

function approachMid(el: LayoutElement, side: ConnectorShiftState) {
  return side === 'shifted' ? getConnectorFarLegMid(el) : getConnectorHomeLegMid(el);
}

/**
 * Each sim frame: start close/transfer when an OHT approaches a leg, advance
 * animations, publish waiting/riding poses, and return layout updates.
 */
export function tickConnectorShifts(params: {
  now: number;
  ohtWorldPositions: Array<{ id: string; x: number; y: number; angle?: number }>;
  elements: Record<string, LayoutElement> | LayoutElement[];
  metersToWorld: (meters: number) => number;
}): ConnectorShiftTickResult {
  const list = Array.isArray(params.elements)
    ? params.elements
    : Object.values(params.elements);

  const busyOhts = connectorShiftStore.getBusyOhtIds();

  for (const pos of params.ohtWorldPositions) {
    if (
      busyOhts.has(pos.id) ||
      connectorShiftStore.isOhtRiding(pos.id) ||
      connectorShiftStore.isOhtWaiting(pos.id)
    ) {
      continue;
    }

    // Park only locks the connector just used — other H's on the same rails
    // must still be able to start a transfer (dual-connector loops).
    const excludeIds: string[] = [];
    const parkedOn = connectorShiftStore.getParkedConnector(pos.id);
    if (parkedOn) {
      const parkedEl = list.find((e) => e.id === parkedOn);
      if (!parkedEl || !isConnectorElement(parkedEl)) {
        connectorShiftStore.releaseParked(pos.id);
      } else {
        const mid = getConnectorSolidLegMid(parkedEl);
        const away = Math.hypot(pos.x - mid.x, pos.y - mid.y);
        if (away > getConnectorReleaseRadius(parkedEl)) {
          connectorShiftStore.releaseParked(pos.id);
        } else {
          excludeIds.push(parkedOn);
        }
      }
    }

    const hit = findConnectorApproachForOht(pos, list, {
      excludeIds,
      headingDeg: pos.angle,
      isBusy: (id) =>
        connectorShiftStore.isAnimating(id) ||
        connectorShiftStore.isCoolingDown(id, params.now) ||
        connectorShiftStore.isOhtCoolingDownOnConnector(pos.id, id, params.now),
    });
    if (!hit) continue;

    const settled = getConnectorShiftState(hit.connector);
    const approach = hit.approachSide;
    const dest = oppositeSide(approach);
    const alongHeadingDeg = alignHeadingToConnectorLeg(
      hit.connector,
      approach,
      pos.angle
    );

    if (!hit.isClosed) {
      connectorShiftStore.start({
        id: hit.connector.id,
        phase: 'closing',
        from: settled,
        to: approach,
        approachSide: approach,
        now: params.now,
        ohtId: pos.id,
        alongHeadingDeg,
      });
    } else {
      connectorShiftStore.start({
        id: hit.connector.id,
        phase: 'transferring',
        from: approach,
        to: dest,
        approachSide: approach,
        now: params.now,
        ohtId: pos.id,
        alongHeadingDeg,
      });
    }
    busyOhts.add(pos.id);
  }

  const finishedIds = connectorShiftStore.tick(params.now, (anim) =>
    anim.phase === 'closing' ? CONNECTOR_CLOSE_DURATION_MS : CONNECTOR_SHIFT_DURATION_MS
  );

  // Chain close→transfer (and land transfers) before publishing poses so the
  // same frame does not drop the OHT between phases.
  const completed: ConnectorShiftComplete[] = [];
  for (const id of finishedIds) {
    const anim = connectorShiftStore.getAnim(id);
    const el = list.find((e) => e.id === id);
    if (!anim || !el || !isConnectorElement(el)) {
      connectorShiftStore.clear(id);
      continue;
    }

    if (anim.phase === 'closing') {
      // Persist closed state, then start the cross-track transfer.
      const closePatch = buildConnectorShiftUpdates(
        el,
        anim.approachSide,
        params.metersToWorld,
        list
      );
      if (closePatch) {
        completed.push({
          ...closePatch,
          // No land yet — OHT still waiting on approach.
        });
      }

      const dest = oppositeSide(anim.approachSide);
      connectorShiftStore.start({
        id,
        phase: 'transferring',
        from: anim.approachSide,
        to: dest,
        approachSide: anim.approachSide,
        now: params.now,
        ohtId: anim.ohtId,
        alongHeadingDeg: anim.alongHeadingDeg,
        force: true,
      });
      continue;
    }

    // transferring complete — land on destination
    const landProgress = anim.to === 'shifted' ? 1 : 0;
    const pose = getConnectorSolidBarPose(el, landProgress);
    const alongHeadingDeg =
      anim.alongHeadingDeg ?? alignHeadingToConnectorLeg(el, anim.to);
    const landAt = {
      x: pose.x,
      y: pose.y,
      angle: alongHeadingDeg,
    };
    const patch = buildConnectorShiftUpdates(el, anim.to, params.metersToWorld, list);
    connectorShiftStore.markSettled(id, params.now);
    if (patch) {
      completed.push({
        ...patch,
        ohtId: anim.ohtId,
        landAt,
        alongHeadingDeg,
      });
    } else if (anim.ohtId) {
      completed.push({
        id,
        updates: {},
        ohtId: anim.ohtId,
        landAt,
        alongHeadingDeg,
      });
    }
  }

  const waiting: ConnectorRidingPose[] = [];
  const riding: ConnectorRidingPose[] = [];

  for (const [connectorId, anim] of connectorShiftStore.getAll()) {
    if (anim.progress >= 1 || !anim.ohtId) continue;
    const el = list.find((e) => e.id === connectorId);
    if (!el || !isConnectorElement(el)) continue;

    if (anim.phase === 'closing') {
      const mid = approachMid(el, anim.approachSide);
      waiting.push({
        ohtId: anim.ohtId,
        connectorId,
        x: mid.x,
        y: mid.y,
        angle:
          anim.alongHeadingDeg ??
          alignHeadingToConnectorLeg(el, anim.approachSide),
      });
      continue;
    }

    // transferring — ride the solid bar (position only; yaw stays on-leg)
    const settled = getConnectorShiftState(el);
    const progress = connectorShiftStore.getSolidProgress(connectorId, settled);
    const pose = getConnectorSolidBarPose(el, progress);
    riding.push({
      ohtId: anim.ohtId,
      connectorId,
      x: pose.x,
      y: pose.y,
      angle:
        anim.alongHeadingDeg ??
        alignHeadingToConnectorLeg(el, anim.approachSide),
    });
  }

  return { completed, riding, waiting };
}

/** Pick travel dir on an edge so motion best matches a world heading (deg). */
export function pickDirForHeading(
  edgePoints: Array<{ x: number; y: number }>,
  headingDeg: number
): 1 | -1 {
  if (edgePoints.length < 2) return 1;
  const a = edgePoints[0];
  const b = edgePoints[edgePoints.length - 1];
  const ex = b.x - a.x;
  const ey = b.y - a.y;
  const elen = Math.hypot(ex, ey) || 1;
  const rad = (headingDeg * Math.PI) / 180;
  const hx = Math.cos(rad);
  const hy = Math.sin(rad);
  const dot = (ex / elen) * hx + (ey / elen) * hy;
  return dot >= 0 ? 1 : -1;
}

/**
 * After an H-connector land, keep the approach heading when there is room to
 * continue; otherwise flip so the OHT does not sit on a dead-end tip twitching.
 */
export function pickDirAfterConnectorLand(
  edgeLength: number,
  dist: number,
  edgePoints: Array<{ x: number; y: number }>,
  headingDeg: number
): 1 | -1 {
  const preferred = pickDirForHeading(edgePoints, headingDeg);
  const remaining = preferred > 0 ? edgeLength - dist : dist;
  const tipEps = Math.min(12, Math.max(2, edgeLength * 0.08));
  if (remaining <= tipEps && edgeLength > tipEps * 2) {
    return preferred > 0 ? -1 : 1;
  }
  return preferred;
}
